import React, { useEffect, useState, useCallback } from "react";
import { Box, Card, Typography, Button, Checkbox, TextField, Chip, IconButton, CircularProgress } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../../../api/client";
import { romanLabel, splitOptions } from "./paperBuilder";

// Render question text as inline markdown (bold, lists, code, etc.).
const MdText = ({ text, sx = {} }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <Typography sx={{ fontSize: "13px", lineHeight: 1.55, ...sx }}>{children}</Typography>,
      li: ({ children }) => <Typography component="li" sx={{ fontSize: "13px", lineHeight: 1.55, ...sx }}>{children}</Typography>,
      code: ({ children }) => (
        <Box component="code" sx={{ backgroundColor: "#eef1f5", borderRadius: "4px", px: 0.5, fontFamily: "monospace", fontSize: "12px" }}>{children}</Box>
      ),
    }}
  >
    {text || ""}
  </ReactMarkdown>
);

const tagBase = { height: "20px", fontSize: "10px", fontWeight: 700, borderRadius: "8px" };
const TAG = {
  type: { ...tagBase, backgroundColor: "#e3f2fd", color: "#1c97e3" },
  level: { ...tagBase, backgroundColor: "#fef3c7", color: "#d97706" },
  marks: { ...tagBase, backgroundColor: "#ede9fe", color: "#7c3aed" },
  bloom: { ...tagBase, backgroundColor: "#dcfce7", color: "#16a34a" },
  sub: { ...tagBase, backgroundColor: "#eef1f5", color: "#5a6473" },
  inPaper: { ...tagBase, backgroundColor: "#e2e8f0", color: "#475569" },
};

// MCQ options under an added question.
const OptionsList = ({ options, pl = 1.5 }) => {
  const opts = splitOptions(options);
  if (!opts.length) return null;
  return (
    <Box sx={{ pl, mt: 0.25 }}>
      {opts.map((o, i) => (
        <Typography key={i} sx={{ fontSize: "12px", color: "#5a6473" }}>{o}</Typography>
      ))}
    </Box>
  );
};

const addOptionIds = (s, opt) => {
  if (!opt) return;
  if (opt.subs) opt.subs.forEach((q) => q && s.add(q.id));
  else s.add(opt.id);
};

// One option (a/b) of an either-or slot in the left panel — plain or split (i/ii).
const EOOption = ({ label, opt, splitMark = [], number, showNumber, onRemove }) => {
  const head = `${showNumber ? `${number}. ` : ""}${label})`;
  if (opt?.subs) {
    return (
      <Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 40 }}>{head}</Typography>
          <Box sx={{ flex: 1 }} />
          {onRemove && <IconButton aria-label="Remove" size="small" onClick={onRemove}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>}
        </Box>
        {opt.subs.map((sub, si) => (
          <Box key={si} sx={{ pl: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, minWidth: 22 }}>{romanLabel(si)})</Typography>
              <Box sx={{ flex: 1 }}><MdText text={sub?.text} sx={{ fontSize: "12px" }} /></Box>
              <Typography sx={{ fontSize: "12px", color: "#7c3aed", fontWeight: 700 }}>({splitMark[si]})</Typography>
            </Box>
            <OptionsList options={sub?.options} pl={3} />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 40 }}>{head}</Typography>
        <Box sx={{ flex: 1 }}><MdText text={opt?.text} /></Box>
        {onRemove && <IconButton aria-label="Remove" size="small" onClick={onRemove}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>}
      </Box>
      <OptionsList options={opt?.options} pl={5} />
    </Box>
  );
};

const usedIdSet = (sections) => {
  const s = new Set();
  sections.forEach((sec) => sec.items.forEach((it) => {
    if (!it) return;
    if (it.subs) it.subs.forEach((q) => q && s.add(q.id)); // list split group
    else if (sec.pattern === "either-or") { addOptionIds(s, it.a); addOptionIds(s, it.b); }
    else s.add(it.id);
  }));
  return s;
};

// Two-panel question selection (FIXED papers). Left = paper sections with fill
// status / reorder / remove; right = the question-bank browser for the active section.
// NOTE: page/size are deliberately NOT sent. Sending { page: 0, size: 500 } made the
// endpoint return "data": [] while still reporting a populated available_filters, so the
// paging is indexed differently than assumed. The panel loads the whole bank at once and
// filters client-side anyway. Re-add only once the indexing convention is confirmed.

const AddQuestionsStep = ({ sections, activeIdx, setActiveIdx, onSectionsChange, category, assignedQuestionBanks, chooseFromExistingQB, assessmentId, filters = {} }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // per-section: show selected questions only when expanded

  const sec = sections[activeIdx];
  // Split marks (e.g. [7, 8]) mean each slot/option holds a group of sub-questions (i, ii).
  const split = sec?.splitMark || [];
  const groupSize = split.length;
  const hasSplit = groupSize > 1;
  const isEO = sec?.pattern === "either-or";
  const isSplit = sec?.pattern === "list" && hasSplit;   // list with sub-divisions
  const eoSplit = isEO && hasSplit;                       // either-or with sub-divisions (a & b each split)

  // The browse uses the page-level Filters (Level / Question Type / Marks / Bloom's).
  const fLevel = filters.level || [];
  const fTypes = filters.question_types || [];
  const fMarks = filters.marks || [];
  const fBlooms = filters.blooms_levels || [];
  const filtersKey = JSON.stringify([fLevel, fTypes, fMarks, fBlooms]);
  // Mapped/Assigned mode: scope the browse to the selected banks. Each carries its own
  // category_id, so banks from different subjects can be combined in one request.
  const bankIds = chooseFromExistingQB
    ? []
    : (assignedQuestionBanks || [])
        .filter((qb) => qb.session_key)
        .map((qb) => ({ category_id: qb.category_id, gen_id: qb.session_key }));
  const bankIdsKey = JSON.stringify(bankIds);

  const fetchQuestions = useCallback(async () => {
    // Scoped either by ids[] (banks picked) or by a real category_id. A MULTI_CATEGORY
    // paper has category.id === null, so it needs banks before it can browse.
    if (!sec || (category?.id == null && !bankIds.length)) { setQuestions([]); return; }
    setLoading(true);
    try {
      const payload = {
        // ids[] entries carry their own category_id, so the top-level key is only
        // needed when browsing all existing banks.
        ...(bankIds.length ? { ids: bankIds } : { category_id: category?.id }),
        question_types: fTypes,
        level: fLevel,
        blooms_levels: fBlooms,
        marks: fMarks,
        search,
      };
      // Only exist once the paper is saved — absent during create.
      if (assessmentId) payload.assessment_id = assessmentId;
      if (sec.paper_section_id) payload.paper_section_id = sec.paper_section_id;

      const res = await api.post("BrowseQuestionsForSection", payload);
      // `data` is either the question array or, since the endpoint took page/size, a paged
      // envelope wrapping it. Accept both so a shape change can't empty the panel silently.
      const raw = res?.data?.data;
      const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.items) ? raw.items
        : Array.isArray(raw?.questions) ? raw.questions
        : Array.isArray(raw?.results) ? raw.results
        : Array.isArray(raw?.content) ? raw.content
        : Array.isArray(res?.data?.questions) ? res.data.questions
        : [];
      if (!Array.isArray(raw) && rows.length === 0) {
        console.log("BrowseQuestionsForSection: unrecognised response shape", res?.data);
      }
      setError(res?.data?.status === "error" ? res?.data?.message || "Could not load questions" : "");
      // Normalise the BrowseQuestionsForSection shape (quiz_id/question) to what this panel uses.
      const list = rows.map((q) => ({
        id: q.quiz_id ?? q.id,
        text: q.question ?? q.text,
        question_type: q.question_type,
        level: q.level,
        marks: q.marks,
        blooms_level: q.blooms_level,
        subcategory: q.subcategory,
        options: q.options,
        image_url: q.image_url ?? q.image ?? null,
        already_in_paper: q.already_in_paper,
      }));
      setQuestions(list);
    } catch (e) {
      console.log("BrowseQuestionsForSection error:", e);
      // Surface it — an empty panel with no explanation reads as "no questions exist".
      setError(e?.response?.data?.message || e?.message || "Could not load questions");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sec, category, filtersKey, search, bankIdsKey, chooseFromExistingQB, assessmentId]);

  // Clear selection / search when switching sections.
  useEffect(() => {
    setSelected(new Set());
    setSearch("");
  }, [activeIdx]);

  // Re-query whenever the page filters / search / section / assigned banks change.
  // (fetchQuestions already lists bankIdsKey + chooseFromExistingQB in its deps, so a
  // bank change re-runs this once — a second effect would double the request.)
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const used = usedIdSet(sections);
  // Question bank hides questions already added to any section; removing one brings it back.
  const visibleQuestions = questions.filter((q) => !used.has(q.id) && !q.already_in_paper);

  const mutateSection = (idx, fn) => {
    const next = sections.map((s, i) => (i === idx ? fn({ ...s, items: [...s.items] }) : s));
    onSectionsChange(next);
  };

  // Slots already filled / still open for the active section, so we never add past count.
  const filledActive = sec ? sec.items.length : 0;
  const remainingSlots = sec ? Math.max(0, Number(sec.count) - filledActive) : 0;
  const sectionFull = !!sec && remainingSlots === 0;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); return n; }
      // For plain list sections, don't let the teacher tick more than the remaining slots.
      if (!isEO && !isSplit && n.size >= remainingSlots) return n;
      n.add(id);
      return n;
    });
  };

  const addSelected = () => {
    const byId = (id) => questions.find((q) => q.id === id);
    const ids = [...selected];
    if (eoSplit) {
      // First `groupSize` selected -> option (a)'s subs, next `groupSize` -> option (b)'s subs.
      if (ids.length !== 2 * groupSize) return;
      const aSubs = ids.slice(0, groupSize).map(byId);
      const bSubs = ids.slice(groupSize).map(byId);
      mutateSection(activeIdx, (s) => { s.items.push({ a: { subs: aSubs }, b: { subs: bSubs } }); return s; });
    } else if (isEO) {
      if (ids.length !== 2) return;
      mutateSection(activeIdx, (s) => { s.items.push({ a: byId(ids[0]), b: byId(ids[1]) }); return s; });
    } else if (isSplit) {
      if (ids.length !== groupSize) return;
      // One slot = a group of sub-questions (i, ii, …), one per split mark.
      mutateSection(activeIdx, (s) => { s.items.push({ subs: ids.map(byId) }); return s; });
    } else {
      mutateSection(activeIdx, (s) => { ids.forEach((id) => { const q = byId(id); if (q) s.items.push(q); }); return s; });
    }
    setSelected(new Set());
  };

  const removeItem = (qi) => mutateSection(activeIdx, (s) => { s.items.splice(qi, 1); return s; });
  const move = (qi, dir) => mutateSection(activeIdx, (s) => {
    const j = qi + dir;
    if (j < 0 || j >= s.items.length) return s;
    [s.items[qi], s.items[j]] = [s.items[j], s.items[qi]];
    return s;
  });

  const n = selected.size;
  const eoNeed = eoSplit ? 2 * groupSize : 2;
  const addLabel = isEO
    ? `Add as Pair${eoSplit ? ` (${n}/${eoNeed})` : ""}`
    : isSplit ? `Add as Set (${n}/${groupSize})` : `Add Selected (${n})`;
  // Block adding once the section's required slots are full; each EO/split add fills one slot.
  const addDisabled = sectionFull
    ? true
    : isEO ? n !== eoNeed : isSplit ? n !== groupSize : (n < 1 || n > remainingSlots);
  const footText = sectionFull
    ? `Section full — ${filledActive}/${sec.count} ${isEO ? "pairs" : "questions"} added`
    : isEO
    ? eoSplit
      ? (n === eoNeed
          ? `${eoNeed} selected → 1 either-or pair (a & b each split ${split.join(" + ")})`
          : `Select ${eoNeed}: first ${groupSize} for (a), next ${groupSize} for (b) — marks ${split.join(", ")}`)
      : (n === 2 ? "2 selected → 1 pair" : "Select exactly 2 to form an either-or pair")
    : isSplit
    ? (n === groupSize
        ? `${groupSize} selected → 1 question (${split.join(" + ")} marks)`
        : `Select exactly ${groupSize} sub-questions (${split.join(", ")} marks)`)
    : (n ? `${n} selected (${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} left)` : `Tick questions to add (${remainingSlots} left)`);

  const appliedChips = [...fTypes, ...fLevel, ...fBlooms, ...fMarks.map((m) => `${m} Marks`)];

  const colSx = { p: 2, borderRadius: "12px", border: "1px solid #f1f3f6", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
      {/* Left — paper sections */}
      <Card sx={colSx}>
        <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1.5 }}>Paper Sections</Typography>
        {sections.map((s, i) => {
          const filled = s.items.length;
          const ok = filled >= s.count;
          const start = s.start || 1;
          return (
            <Card
              key={i}
              sx={{
                p: 1.5, mb: 1.5, borderRadius: "10px", boxShadow: "none",
                border: i === activeIdx ? "1px solid #1c97e3" : "1px solid #f1f3f6",
                ...(i === activeIdx ? { boxShadow: "0 0 0 2px rgba(28,151,227,0.1)" } : {}),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>{s.title}</Typography>
                <Chip size="small" label={s.pattern === "list" ? "List" : "Either-Or"} sx={{ ...tagBase, backgroundColor: "#eef2ff", color: "#4f46e5" }} />
                {(s.splitMark || []).length > 1 ? (
                  <Chip size="small" label={`Split ${s.splitMark.join(" + ")} = ${s.splitMark.reduce((a, b) => a + Number(b), 0)} Marks`} sx={TAG.marks} />
                ) : (
                  s.marks != null && <Chip size="small" label={`${s.marks} Marks`} sx={TAG.marks} />
                )}
                {s.choice && <Chip size="small" label={`Answer ${s.choice.must_answer}/${s.choice.total_given}`} sx={{ ...tagBase, backgroundColor: "#fef9c3", color: "#a16207" }} />}
                <Chip
                  size="small"
                  label={`${filled}/${s.count} ${s.pattern === "either-or" ? "pairs" : "questions"}`}
                  sx={{ ...tagBase, ml: "auto", backgroundColor: ok ? "#dcfce7" : "#fef3c7", color: ok ? "#16a34a" : "#b45309" }}
                />
              </Box>
              <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant={i === activeIdx ? "contained" : "outlined"}
                  onClick={() => setActiveIdx(i)}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Browse questions
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={filled === 0}
                  onClick={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
                  endIcon={expanded[i] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  {expanded[i] ? "Hide" : "View"} Selected ({filled})
                </Button>
              </Box>

              {expanded[i] && (
              <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                {s.items.map((it, qi) =>
                  !it ? null :
                  it.subs ? (
                    // Split list slot — sub-questions i) / ii) with their marks.
                    <Box key={qi} sx={{ backgroundColor: "#fafbfc", border: "1px solid #f1f3f6", borderRadius: "8px", p: 0.75 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 26 }}>{start + qi}.</Typography>
                        <Box sx={{ flex: 1 }} />
                        <IconButton aria-label="Move up" size="small" onClick={() => move(qi, -1)} disabled={qi === 0}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton aria-label="Move down" size="small" onClick={() => move(qi, 1)} disabled={qi === s.items.length - 1}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton aria-label="Remove question" size="small" onClick={() => removeItem(qi)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                      {it.subs.map((sub, sidx) => (
                        <Box key={sidx} sx={{ pl: 2 }}>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, minWidth: 22 }}>{romanLabel(sidx)})</Typography>
                            <Box sx={{ flex: 1 }}><MdText text={sub?.text} sx={{ fontSize: "12px" }} /></Box>
                            <Typography sx={{ fontSize: "12px", color: "#7c3aed", fontWeight: 700 }}>({(s.splitMark || [])[sidx]})</Typography>
                          </Box>
                          <OptionsList options={sub?.options} pl={3} />
                        </Box>
                      ))}
                    </Box>
                  ) : s.pattern === "list" ? (
                    <Box key={qi} sx={{ backgroundColor: "#fafbfc", border: "1px solid #f1f3f6", borderRadius: "8px", p: 0.75 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "13px", minWidth: 26 }}>{start + qi}.</Typography>
                        <Box sx={{ flex: 1 }}><MdText text={it.text} /></Box>
                        <IconButton aria-label="Move up" size="small" onClick={() => move(qi, -1)} disabled={qi === 0}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton aria-label="Move down" size="small" onClick={() => move(qi, 1)} disabled={qi === s.items.length - 1}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton aria-label="Remove question" size="small" onClick={() => removeItem(qi)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                      <OptionsList options={it.options} pl={3.5} />
                    </Box>
                  ) : (
                    <Box key={qi} sx={{ backgroundColor: "#fafbfc", border: "1px solid #f1f3f6", borderRadius: "8px", p: 0.75 }}>
                      <EOOption label="a" opt={it.a} splitMark={s.splitMark || []} number={start + qi} showNumber onRemove={() => removeItem(qi)} />
                      <Typography sx={{ textAlign: "center", fontStyle: "italic", color: "#8e9ebc", fontSize: "12px" }}>(or)</Typography>
                      <EOOption label="b" opt={it.b} splitMark={s.splitMark || []} number={start + qi} />
                    </Box>
                  )
                )}
              </Box>
              )}
            </Card>
          );
        })}
      </Card>

      {/* Right — question bank browser */}
      <Card sx={colSx}>
        <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1.5 }}>Question Bank</Typography>
        {!sec ? (
          <Typography sx={{ color: "#8e9ebc", textAlign: "center", p: 3 }}>Select a section's “Browse questions” to start adding.</Typography>
        ) : (
          <>
            <Box sx={{ mb: 1, fontSize: "12px", color: "#8e9ebc", display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              <b>{sec.title}</b> — filters:
              {appliedChips.length
                ? appliedChips.map((c, ci) => <Chip key={ci} size="small" label={c} sx={{ ...tagBase, backgroundColor: "#e3f2fd", color: "#1c97e3" }} />)
                : <span>All</span>}
            </Box>
            <TextField size="small" fullWidth placeholder="Search questions…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1.5 }} />
            <Box sx={{ maxHeight: 430, overflow: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
              {loading ? (
                <Box sx={{ textAlign: "center", p: 3 }}><CircularProgress size={24} /></Box>
              ) : error ? (
                <Box sx={{ p: 2, backgroundColor: "#fff0f0", border: "1px solid #f1aeae", borderRadius: "8px" }}>
                  <Typography sx={{ color: "#b91c1c", fontSize: "13px", fontWeight: 600 }}>
                    Couldn't load questions — {error}
                  </Typography>
                </Box>
              ) : visibleQuestions.length ? (
                visibleQuestions.map((q) => {
                  return (
                    <Box key={q.id} sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1, border: "1px solid #f1f3f6", borderRadius: "8px" }}>
                      <Checkbox
                        size="small"
                        checked={selected.has(q.id)}
                        // Full section, or plain-list cap reached → block new ticks (already-ticked stay togglable).
                        disabled={!selected.has(q.id) && (sectionFull || (!isEO && !isSplit && selected.size >= remainingSlots))}
                        onChange={() => toggleSelect(q.id)}
                        sx={{ p: 0.5 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <MdText text={q.text} />
                        {q.image_url && (
                          <Box
                            component="img"
                            src={q.image_url}
                            alt="question"
                            sx={{ display: "block", maxWidth: "100%", maxHeight: 160, mt: 0.5, borderRadius: "8px", border: "1px solid #f1f3f6", objectFit: "contain" }}
                          />
                        )}
                        {splitOptions(q.options).length > 0 && (
                          <Box sx={{ pl: 1.5, mt: 0.25 }}>
                            {splitOptions(q.options).map((o, oi) => (
                              <Typography key={oi} sx={{ fontSize: "12px", color: "#5a6473" }}>{o}</Typography>
                            ))}
                          </Box>
                        )}
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                          <Chip size="small" label={q.question_type} sx={TAG.type} />
                          <Chip size="small" label={q.level} sx={TAG.level} />
                          <Chip size="small" label={`${q.marks} Marks`} sx={TAG.marks} />
                          {q.blooms_level && <Chip size="small" label={q.blooms_level} sx={TAG.bloom} />}
                          {q.subcategory && <Chip size="small" label={q.subcategory} sx={TAG.sub} />}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{ color: "#8e9ebc", textAlign: "center", p: 3 }}>
                  {/* Nothing was requested yet — say so rather than implying an empty result. */}
                  {category?.id == null && !bankIds.length
                    ? "Select one or more assigned question banks above to browse questions."
                    : "No questions match this section's filters."}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#8e9ebc" }}>{footText}</Typography>
              <Button variant="contained" size="small" disabled={addDisabled} onClick={addSelected} sx={{ textTransform: "none", borderRadius: "10px" }}>
                {addLabel}
              </Button>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

export default AddQuestionsStep;
